import './OfferRevaluate.scss';
import { useState } from 'react';
import Button from '@srm/ui/button';
import clsx from 'clsx';
import Modal from '@srm/ui/modal';
import { IOffer } from '@models/winnerSelection/ITableDemand';
import { Controller, useForm } from 'react-hook-form';
import { WinnerSelectionService } from '@services/WinnerSelectionService';
import { sendNotification } from '@srm/ui/lib/notifications';
import Hint from '@srm/ui/hint';
import { IOfferRevaluateFormData } from './IOfferRevaluateFormData';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@srm/ui/controls/HookFormProvider';
import TextareaControl from '@srm/ui/controls/TextareaControl';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';

export interface IOfferRevaluateProps {
    tableKey: string;
    isEdit: boolean;
    value: IOffer;
    demandId: string;
    onSubmit?: (value: IOfferRevaluateFormData) => void;
    onClose?: () => void;
}

const OfferRevaluate = ({ tableKey, isEdit, value, demandId, onSubmit, onClose }: IOfferRevaluateProps) => {
    const [error, setError] = useState<string>();
    const methods = useForm<IOfferRevaluateFormData>({
        mode: 'onBlur',
        defaultValues: { revaluateComment: '' },
    });

    const { isSubmitting, isDirty } = methods.formState;
    // Escape и клик по фону закрывают окно: набранный комментарий без вопроса не теряем
    const hasUnsavedInput = useUnsavedFormInput(methods, { textFields: ['revaluateComment'] });
    const close = () => {
        if (!isSubmitting) onClose?.();
    };

    const onFormSubmit = async (form: IOfferRevaluateFormData) => {
        setError(undefined);
        return WinnerSelectionService.saveTableDemandOfferRevaluate(value.key, demandId, tableKey, form)
            .then(() => {
                sendNotification({
                    message: 'Данные успешно сохранены',
                    variant: 'green',
                });
                onSubmit && onSubmit(form);
            })
            .catch((reason) => {
                setError(reason);
                console.log(reason);
            });
    };

    const errorLabel = error && (
        <div className="offer-revaluate__error">
            <Hint role="alert" title={error} tone="danger" />
        </div>
    );

    const modalFooter = (
        <div className={'modal-dlg-buttons'}>
            <Button
                type="submit"
                form="offer-revaluate-form"
                loading={isSubmitting}
                className={clsx('offer-revaluate__button', 'offer-revaluate__save-button')}
                disabled={isSubmitting || !isDirty}
            >
                ОК
            </Button>
            <Button onClick={close} variant="light">
                Отмена
            </Button>
        </div>
    );

    return (
        <Modal
            size="xl"
            onClose={close}
            hasUnsavedInput={() => isEdit && hasUnsavedInput()}
            closeDisabled={isSubmitting}
            header={`Отправить предложение №${value.number || ''} (${value.name}) на переоценку?`}
            footer={isEdit ? modalFooter : null}
        >
            {errorLabel}
            <FormProviderControl
                formMethods={methods}
                id="offer-revaluate-form"
                className="offer-revaluate__form"
                onSubmit={onFormSubmit}
            >
                <HookFormProvider controller={Controller}>
                    <div className="offer-revaluate__content">
                        <div className="offer-revaluate__row">
                            <TextareaControl
                                label="Комментарий"
                                name="revaluateComment"
                                readOnly={!isEdit}
                                disabled={!isEdit}
                                autoResize={true}
                                control={methods.control}
                            />
                        </div>
                    </div>
                </HookFormProvider>
            </FormProviderControl>
        </Modal>
    );
};

export default OfferRevaluate;
